import type { Credentials } from '@studio/models/auth';
import { Protocol as ControlProtocol } from '@studio/models/control';
import type { Configuration as OnvifConfig } from '@studio/models/control/onvif';
import { ViscaIpDefaults } from '@studio/models/control/visca-ip';
import { Protocol as VideoProtocol, VideoDefaults } from '@studio/models/video';
import { RtspDefaults } from '@studio/models/video/rtsp';
import { toEnum } from '@tryiris-ai/js-types';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import type { Discovery } from '#~/device/discovery/index.js';
import type { Minimist } from '#~/device/index.js';
import type { Template } from '#~/device/registry/index.js';
import type { Model } from '@studio/models/device';
import { Filter } from './types.js';
import { cameraHost } from './discovery-transports.js';
import { resolveControlSettings } from '../control/normalize.js';

/**
 * Transforms the specified value to a `Filter` enumeration.
 *
 * @param value - The value to be transformed.
 * @returns The transformed `Filter` enumeration or `Filter.All`.
 */
export const toFilter = (value: unknown): Filter => toEnum(Filter, value, { default: Filter.All });

/**
 * Pure resolver for the brand-only fingerprint seed (extended discovery —
 * Change 2B). `panel.tsx`'s `handleSelectionChange` writes
 * `source.seededBrandId` so the RTSP Helper (rtsp.tsx) can pre-seed its Brand
 * dropdown when the user arrived via a brand-only discovery match. The seed is
 * set ONLY on a brand-only row (a `brandId` with NO `modelId`) and CLEARED on
 * every other path — the full-model match and the legacy (no-model) sync — so a
 * stale seed from a prior selection never leaks into a later one.
 *
 * Extracted pure (here, not in panel.tsx) so this set/clear contract is testable
 * without importing the panel component graph — panel.tsx pulls in
 * content.jsx → scaffold.tsx, a circular import chain that cannot be evaluated
 * in isolation under the current ESM jest setup.
 *
 * Per `tests-require-user-story-comment` + `think-in-systems` (ai.knowledge/global).
 */
export const resolveSeededBrandId = <B>(
  selection: T_Maybe<{ brandId?: T_Maybe<B>; modelId?: T_Maybe<unknown> }>
): T_Maybe<B> => (selection?.brandId && !selection.modelId ? selection.brandId : undefined);

/**
 * Brand-only host seed (BUG 1 producer contract). A brand-only discovery must
 * NOT sync its `video` aspect into `candidate` (that spins up a preview device
 * and a spurious "Connection failed" before the user picks anything), so the
 * discovered host is carried out-of-band and re-seeds the IP field on mount.
 *
 * Returns the discovered host ONLY for a brand-only match (brandId, no
 * modelId), and `undefined` on every other selection — the same no-leak
 * contract as resolveSeededBrandId, so switching to a known-model/legacy
 * selection clears the stale host. Extracted pure for the same reason
 * (panel.tsx cannot be imported in isolation — see resolveSeededBrandId).
 *
 * Per `tests-require-user-story-comment` + `think-in-systems` (ai.knowledge/global).
 */
export const resolveSeededHost = (
  // `video` is typed `unknown` (not `{ host }`) because a Discovery's video
  // aspect is the wide `Configuration` union — only the RTSP shape carries a
  // host — so we narrow with a cast, mirroring panel.tsx's original access.
  selection: T_Maybe<{ brandId?: T_Maybe<unknown>; modelId?: T_Maybe<unknown>; video?: unknown }>
): T_Maybe<string> =>
  selection?.brandId && !selection.modelId
    ? ((selection.video as T_Maybe<{ host?: T_Maybe<string> }>)?.host ?? undefined)
    : undefined;

/**
 * Bare RTSP video marker for a brand-only selection (BUG-fix: blank Configure-
 * video screen). A brand-only match must carry a `candidate.video` so
 * content.tsx (`isRtsp(candidate.video?.protocol) && <Rtsp/>`) renders the RTSP
 * Helper — stripping video entirely left the protocol undefined and the screen
 * blank. But it must have NO host, so the preview Mediator's `toUrx` builds no
 * connectable device (→ no spurious "Connection failed"). Mirrors the proven
 * `handleManualRtsp` construction (footer.tsx): `defaults({host: undefined,
 * ...RtspDefaults}, VideoDefaults)`. The discovered host is seeded separately
 * via resolveSeededHost to pre-fill the IP field.
 *
 * Per `think-in-systems` + `reference-rules-in-comments` (ai.knowledge/global).
 */
export const buildBrandOnlyVideoMarker = () =>
  defaults({ host: undefined, ...RtspDefaults }, VideoDefaults);

/**
 * Reset the candidate for a fresh "Manually Configure an RTSP source" add
 * (ENG-4159 BUG B4). The manual-add affordance (`handleManualRtsp` in
 * `source/footer.tsx`) must open the Configure-video screen with a clean slate.
 *
 * It is the INVERSE of the two writes that seed a candidate from a discovery
 * selection — `applyCredentialsToCandidate` (below) and the model fingerprint —
 * so every field those writes touch is a field this reset clears:
 *   - Replaces `candidate.video` with a bare, host-less RTSP marker, keeping the
 *     spread of the prior video config so the reset's handling of every other
 *     video field is unchanged, then clearing `host` (blank IP field, no doomed
 *     preview device) and `credentials` (see the creds-leak note below).
 *   - Clears `candidate.modelId` — the B4 fix. `rtsp.tsx` seeds its Model
 *     dropdown from `candidate.modelId` (:304) and DERIVES the Brand from that
 *     model's registry template (:557-559), so a modelId left over from a prior
 *     discovery selection bled BOTH the brand and model into a manual add.
 *     Clearing it blanks both dropdowns.
 *   - Clears `candidate.control.credentials` — the credentials sibling of B4
 *     (see below).
 *
 * The credentials leak (ENG-4159, B4's sibling): authenticating a discovered
 * camera folds the entered creds onto the candidate via
 * `applyCredentialsToCandidate`, which writes BOTH `video.credentials` AND
 * `control.credentials`. The manual RTSP screen seeds its username/password
 * inputs from `videoCreds?.username ?? controlCreds?.username`
 * (`video/rtsp/rtsp.tsx` :376/:379) — a `??` fallback across the two planes —
 * so BOTH must be cleared or the surviving plane re-leaks through the fallback.
 * Clearing exactly the planes the fold writes keeps the two symmetric: the next
 * field the fold learns to write cannot become a third silent leak.
 *
 * Deliberately scoped to `candidate`: the intentional Change-2B brand-only
 * fingerprint seed lives in a SEPARATE store slice (`source.seededBrandId`),
 * which this reset never touches — so a brand-only discovery match still
 * pre-seeds its brand + prefills its host.
 *
 * Extracted pure (a draft-safe mutator) so the reset contract is unit-testable
 * without rendering `<Footer>` — which pulls in the wiring/billing graph that
 * makes the component-level `.tsx` suite heavy under the current ESM jest setup
 * (same rationale as `resolveSeededBrandId` / `buildBrandOnlyVideoMarker`).
 *
 * Per `reference-rules-in-comments` + `think-in-systems` (ai.knowledge/global).
 */
export const applyManualRtspReset = (
  // `modelId` is `readonly` on `Minimist`, but this helper is only ever called
  // with the store's immer draft (`draft.candidate`), where immer's WritableDraft
  // makes it assignable — rtsp.tsx's `syncSelection` writes it the same way. The
  // param re-declares `modelId` as a writable optional so the clear type-checks
  // without casting at the call site. `video` and `control` are already writable
  // optionals on Minimist (`Settings` is `T_Optional`), so neither needs a cast.
  candidate: Pick<Minimist, 'control' | 'video'> & { modelId?: Minimist['modelId'] }
): void => {
  // `credentials: undefined` goes last so it wins the spread. `defaults` can't
  // refill it — neither `VideoDefaults` ({}) nor `RtspDefaults` declares it.
  candidate.video = defaults(
    { ...candidate.video, host: undefined, ...RtspDefaults, credentials: undefined },
    VideoDefaults
  );
  candidate.modelId = undefined;

  // Guard mirrors `applyCredentialsToCandidate` exactly: a control shape the fold
  // never writes creds into (VISCA/Serial, NONE) is one this reset leaves alone.
  const { control } = candidate;
  if (
    control?.protocol === ControlProtocol.ONVIF ||
    control?.protocol === ControlProtocol.HTTP ||
    control?.protocol === ControlProtocol.VISCA_IP
  ) {
    candidate.control = { ...control, credentials: undefined };
  }
};

/**
 * Resolve the committed device name for a single-add (ENG-4159 follow-up).
 *
 * A brand+model add already carries a name: `candidate.name` (the template /
 * model nickname written in `source/panel.tsx`, or typed into the name field)
 * or, failing that, the `defaultName` the manufacturer step derived via
 * `getNickname`. A MODEL-LESS add carries neither, so the commit previously
 * fell through to `DeviceDefaults.name` ("Unknown") in `toDevice`
 * (`device/utils.ts`). Instead, name it from the device's host/IP — prefer the
 * video host (the RTSP IP the user typed / discovery resolved), then the
 * control host.
 *
 * Precedence is IDENTICAL to the prior `defaults({}, candidate, { name:
 * defaultName })` for the known-model case (candidate.name → defaultName); the
 * host fallback is strictly additive and fires only when both are absent, so
 * brand+model naming is byte-identical. Returns `undefined` when nothing
 * resolves (a truly hostless candidate) — the caller then lets
 * `toDevice`'s `defaults(rest, DeviceDefaults)` apply the "Unknown" final
 * fallback exactly as before.
 *
 * Extracted pure (no store / immer) so the fallback chain is unit-testable
 * without rendering the wiring hook graph — the same ESM-jest rationale as the
 * sibling helpers in this file (`resolveSeededBrandId`, `buildBulkCandidate`,
 * …). Per `tests-require-user-story-comment` + `think-in-systems`
 * (ai.knowledge/global).
 */
export const resolveModelLessDeviceName = (
  candidate: T_Maybe<Pick<Minimist, 'name' | 'video' | 'control'>>,
  defaultName: T_Maybe<string>
): T_Maybe<string> => {
  // Known-model naming is authoritative — return it untouched.
  if (candidate?.name) return candidate.name;
  if (defaultName) return defaultName;

  // Model-less: fall back to the host/IP. `'host' in` narrows the union-shaped
  // video/control Configurations (RTSP/VISCA/ONVIF/HTTP carry a host; NDI/
  // browsermedia video may not) so we read the field without a cast.
  const videoHost =
    candidate?.video && 'host' in candidate.video ? candidate.video.host : undefined;
  const controlHost =
    candidate?.control && 'host' in candidate.control ? candidate.control.host : undefined;

  // `||` (not `??`) so an empty-string host also falls through to the next
  // source and ultimately to DeviceDefaults, never committing a blank name.
  return videoHost || controlHost || undefined;
};

/**
 * Clear the brand-only fingerprint seeds for a fresh "Manually Configure an RTSP
 * source" add (B12). The manual affordance opens a BLANK slate — brand, model,
 * and IP all empty. `rtsp.tsx` pre-seeds its Brand dropdown from
 * `source.seededBrandId` and its IP field from `source.seededHost` on mount, so a
 * previously-selected brand-only discovery row would bleed a brand + IP into the
 * "blank" manual screen unless those seeds are cleared here. The BLANK contract
 * wins over the leftover seed.
 *
 * Complements `applyManualRtspReset`, which owns the `candidate` slice; this owns
 * the two `source` seeds the candidate reset deliberately does not reach.
 * Extracted pure (a draft-safe mutator) so the clear is unit-testable without
 * rendering `<Footer>` — same rationale as `applyManualRtspReset`.
 *
 * Per `reference-rules-in-comments` + `think-in-systems` (ai.knowledge/global).
 */
export const clearManualRtspSeeds = (source: {
  seededBrandId?: T_Maybe<unknown>;
  seededHost?: T_Maybe<string>;
}): void => {
  source.seededBrandId = undefined;
  source.seededHost = undefined;
};

/**
 * Seed the RTSP helper from a discovery whose video never resolved (an RTSP (ONVIF)
 * camera whose probe settled without a usable stream URI). Used by Continue in
 * source/footer.tsx and by the Configure video fallback in video/content.tsx.
 *
 *   - `candidate.video` becomes the bare, host-less RTSP marker the brand-only
 *     route uses (`buildBrandOnlyVideoMarker`): content.tsx renders the RTSP
 *     helper, and with no host the preview Mediator builds no doomed device.
 *   - `source.seededHost` gets the camera's host (`cameraHost`: discovery
 *     address, else the transport host) so the helper's IP field is pre-filled.
 *     A host already seeded is kept when the selection has none.
 *
 * Credentials are deliberately untouched: the ONVIF credentials stay on
 * `candidate.control`, and rtsp.tsx's username/password seed falls back to them.
 * Draft-safe mutator, extracted pure for unit tests (same rationale as
 * `applyManualRtspReset`). Per rules: think-in-systems, reference-rules-in-comments
 * (ai.knowledge/global).
 */
export const seedRtspHelperFromDiscovery = (
  draft: {
    candidate: Pick<Minimist, 'video'>;
    source: { seededHost?: T_Maybe<string> };
  },
  selection: T_Maybe<Discovery>
): void => {
  draft.candidate.video = buildBrandOnlyVideoMarker();
  draft.source.seededHost =
    (selection ? cameraHost(selection) : undefined) ?? draft.source.seededHost;
};

/**
 * Known-model candidate substitution (W4 helper, extracted per W7 to enable
 * targeted unit tests of the branch-choosing + URX-assembly logic without
 * standing up the full `<Discovery>` component tree).
 *
 * Per `reference-rules-in-comments`: covered by unit tests in `discovery.test.ts`.
 */
export const buildKnownModelCandidate = (
  selection: T_Maybe<Discovery>,
  template: T_Maybe<Template>,
  credentials: T_Maybe<OnvifConfig['credentials']> | Credentials,
  selectionHost: T_Maybe<string>
): T_Maybe<T_Optional<Pick<Minimist, 'video' | 'control'>>> => {
  if (!selectionHost) return undefined;
  if (!selection) return undefined;

  // NDI branch: video from the selection, control from the template.
  if ('video' in selection && selection.video?.protocol === VideoProtocol.NDI) {
    const result: Pick<Minimist, 'video' | 'control'> = {
      // Clone to avoid sharing an immer draft reference with the selection.
      video: { ...selection.video }
    };

    if (template?.control !== undefined) {
      result.control = defaults({}, template.control, { host: selectionHost, credentials });
    }
    return result;
  }

  // RTSP branch: template.video carries protocol/port/path; host + creds
  // come from the selection. No template video means no change.
  if (!template?.video) return undefined;

  const result: Pick<Minimist, 'video' | 'control'> = {
    video: defaults({ host: selectionHost, credentials }, template.video)
  };

  if (template.control !== undefined) {
    result.control = defaults({}, template.control, { host: selectionHost, credentials });
  }
  return result;
};

/**
 * Pick a brand's generic model id via the authoritative `Model.generic`
 * bit (the registry-declared generic/"Other" entry).
 *
 * Mirrors the single-brand wizard resolution, lifted to a pure helper so
 * the Multi-Add selector can resolve N brands' generic models without
 * calling `useModels` in a loop and can unit-test the match rule directly.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global): matches on the
 * authoritative `Model.generic` flag (set in
 * `api/device/registry/utils.ts`), not a name substring — precedent
 * `routes/room/watchdog/calibration.tsx:232-238` (ENG-4495 C7) replaced
 * the same fragile `name.includes('other')` heuristic because "Brother"
 * false-matched. Tested in `utils.test.ts`.
 */
export const pickOtherModelId = (
  models: T_Maybe<Record<Model['id'], Model>>
): T_Maybe<Model['id']> => {
  if (!models) return undefined;
  // Match on the authoritative generic flag — resilient to catalog names
  // like "Brother 5000" that a name substring match false-positives on,
  // and to generic entries whose name lacks "other".
  //
  // ENG-4924: a not-yet-supported model is also `generic` (it runs on the
  // generic template) and sorts before "Other", so it is excluded here, as
  // api/device/registry/utils.ts `isGenericTemplate` excludes it.
  const match = Object.values(models).find(
    (model) => model.generic === true && !model.notYetSupported
  );
  return match?.id;
};

/**
 * Build the `{ video, control, modelId }` candidate slice for a
 * BRAND-detected discovery (extended discovery matched a brand
 * fingerprint but not a specific model — `brandId` set, `modelId` unset).
 *
 * Peer of `buildKnownModelCandidate`: the brand's generic "Other" model's
 * registry template drives `{ video, control }`, and the resolved "Other"
 * model id is carried on the candidate so the committed tile reads
 * "{Brand} / Other" with the registry URX instead of the blank protocol
 * defaults (rtsp /1 + VISCA 52381). Credentials are intentionally EMPTY —
 * the same add-then-authenticate contract the known-model bulk path uses
 * (the user authenticates later from Advanced Settings), so
 * `buildKnownModelCandidate` is reused with `credentials: undefined` to
 * keep the two peer paths byte-identical.
 *
 * Returns `undefined` when the model id or template is missing, or when
 * `buildKnownModelCandidate` cannot materialize a video block (e.g. the
 * template carries no video) — the caller then falls back to the protocol
 * defaults so a missing/loading/errored template never loses the add.
 *
 * Generic "Other" templates have their control cleared in the registry
 * (`api/device/registry/utils.ts`: `ptzDriver` blanked for `model.generic`),
 * so `buildKnownModelCandidate` yields video-only for them. Per spec, config
 * not sourced from the registry falls back to defaults — so a control-less
 * built candidate gets the VISCA/IP default control (UDP 52381) keyed to the
 * host, matching the RTSP/NDI default branches of `buildBulkCandidate`.
 * Otherwise brand-detected adds would ship control-less (no PTZ).
 *
 * Per `think-in-systems` (ai.knowledge/global): a peer branch of the
 * known-model path — both the generic model IDENTITY and its template are
 * threaded, never a template alone.
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
export const buildBrandDetectedCandidate = (
  selection: T_Maybe<Discovery>,
  modelId: T_Maybe<Model['id']>,
  template: T_Maybe<Template>,
  selectionHost: T_Maybe<string>
): T_Maybe<T_Optional<Pick<Minimist, 'video' | 'control' | 'modelId'>>> => {
  if (!modelId || !template) return undefined;
  // Empty credentials: brand-detected sources authenticate later, exactly
  // like the known-model bulk path — reuse its builder so video/control
  // assembly stays identical across the two peer branches.
  const built = buildKnownModelCandidate(selection, template, undefined, selectionHost);
  if (!built) return undefined;
  // Generic "Other" templates have their control cleared in the registry
  // (ptzDriver blanked for model.generic), so the registry yields video-
  // only. Config not sourced from the registry falls back to defaults —
  // apply the VISCA/IP default control (UDP 52381) so brand-detected adds
  // are not shipped control-less.
  const control =
    built.control ??
    (selectionHost ? defaults({ host: selectionHost }, ViscaIpDefaults) : undefined);
  // Carry the resolved generic-model identity so the fan-out commits it
  // (a template alone cannot set the tile's Model — see wiring.ts).
  return { ...built, control, modelId };
};

/**
 * True when the selected discovery is a known model that should take the
 * HTTP credential-probe / control-attach branch. Gate on control, not
 * template.video: pbToJsVideoTemplate materializes RTSP only, so NDI video
 * comes from the discovery's own config and still needs the probe.
 *
 * Per `reference-rules-in-comments`: covered by unit tests in `utils.test.ts`.
 */
export const shouldProbeKnownModel = (
  selection: T_Maybe<Discovery>,
  template: T_Maybe<Template>
): boolean => {
  if (!selection || !('video' in selection)) return false;
  if (!selection.modelId || selection.irisEnabled) return false;
  if (!template) return false;

  // RTSP known-model: the template carries the video contract.
  if (template.video) return true;

  // NDI known-model: template.video is undefined by design, so run whenever
  // the template still carries a control contract to attach.
  return selection.video?.protocol === VideoProtocol.NDI && template.control !== undefined;
};

/**
 * Pure gate for the multi-add "selection cap reached" message.
 *
 * The multi-add source selector disables the REMAINING (unchecked) row
 * checkboxes once the current selection reaches the plan's remaining-source
 * cap, but nothing explained WHY the user could not keep ticking. This
 * predicate decides when the bulk footer surfaces a prominent explanation
 * (`<SelectionCapReached>`, rendered in the SAME footer slot as the single-add
 * `<LimitExceeded>`) — shown ONLY in the "your selection filled the remaining
 * slots" case:
 *
 *   - `isBulkAddEnabled` — bulk-add mode is active (single-add uses its own
 *     footer callout).
 *   - `cap > 0` — there IS remaining headroom under the plan. When `cap === 0`
 *     the user is ALREADY at their plan max and the footer's `<LimitExceeded>`
 *     covers it; showing this too would double-message, so we return `false`
 *     (the footer additionally gates the two on `limitCanAddCamera()`, keeping
 *     them mutually exclusive).
 *   - `selectedCount >= cap` — the selection has reached (or exceeded) the cap,
 *     so further rows are blocked.
 *
 * Extracted pure (here, not in footer.tsx) so the show/hide contract is
 * testable without importing the footer component graph — which pulls in a
 * transitive `lodash-es` import that makes the component-level `.tsx` suite
 * un-runnable under the current ESM jest setup.
 *
 * Per `tests-require-user-story-comment` + `think-in-systems` (ai.knowledge/global).
 */
export const shouldShowSelectionCapMessage = (input: {
  isBulkAddEnabled: boolean;
  cap: number;
  selectedCount: number;
}): boolean => input.isBulkAddEnabled && input.cap > 0 && input.selectedCount >= input.cap;

/**
 * Fold submitted credentials into the candidate's control and (RTSP-family)
 * video slots. Control creds: ONVIF/HTTP/VISCA_IP; video creds: RTSP/RTSPT/
 * RTSPLT. The RTSP-video fold prevents the UNSUPPORTED+RTSP-auth modal loop.
 *
 * Per `reference-rules-in-comments`: the RTSP-video fold is the regression fix
 * for the UNSUPPORTED+RTSP-auth infinite-modal-loop.
 */
export const applyCredentialsToCandidate = (
  candidate: Pick<Minimist, 'control' | 'video'>,
  credentials: T_Maybe<Credentials>
): void => {
  const { control } = candidate;
  if (
    control?.protocol === ControlProtocol.ONVIF ||
    control?.protocol === ControlProtocol.HTTP ||
    control?.protocol === ControlProtocol.VISCA_IP
  ) {
    candidate.control = { ...control, credentials };
  }

  const video = candidate.video;
  if (
    video &&
    'protocol' in video &&
    (video.protocol === VideoProtocol.RTSP ||
      video.protocol === VideoProtocol.RTSPT ||
      video.protocol === VideoProtocol.RTSPLT)
  ) {
    candidate.video = { ...video, credentials };
  }
};

/**
 * Build a candidate `{ video, control }` slice for bulk-add commit
 * (Multi-Add Phase 1, W3). Pure function — no side effects, no I/O.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global): this helper
 * is the single point where the 5 source types defined by §1.5 are
 * mapped to a commit-ready candidate. Tested in `utils.test.ts` with
 * one user-story per branch.
 *
 * Per `think-in-systems` (ai.knowledge/global): pure-function extraction
 * lets the bulk-commit pipeline (`handleBulkCommit`, W7) iterate without
 * duplicating the branch logic in `panel.tsx` / `discovery.tsx`.
 *
 * Branches (priority ordered to mirror the matrix; first match wins):
 *
 *   1. Known model (template + selectionHost): delegate to
 *      `buildKnownModelCandidate` — same logic the single-add flow uses.
 *      Reused so the bulk path is byte-identical to single-add for
 *      fingerprint-matched discoveries (Q1 honored).
 *
 *   2. NDI: clone `discovery.video` (NDI carries no credentials in the
 *      video config — auth is HTTP-only); default control to VISCA UDP
 *      (`ViscaIpDefaults`). Plan §1.5 — VISCA is the canonical no-auth
 *      control transport for unfingerprinted NDI sources.
 *
 *   3. RTSP family (RTSP / RTSPT / RTSPLT / IRIS-as-RTSP): use
 *      `discovery.video` as-is (creds may already be folded in by
 *      `applyCredentialsToCandidate`); default control to VISCA UDP.
 *      Plan §1.5 — IRIS-as-RTSP shares the RTSP video transport.
 *
 *   4. ONVIF (control-only discovery): use `discovery.control` as the
 *      control configuration; synthesize a best-guess RTSP URI for video
 *      (`rtsp://host:554/`) since P1 explicitly skips the ONVIF capability
 *      probe (known limitation — degraded ONVIF without capability
 *      probe).
 *
 *   5. Local source (browsermedia): use `discovery.video` (browsermedia
 *      URI). Defensive guard: missing/empty `path` → `undefined` so the
 *      caller can skip+log instead of committing garbage. No control —
 *      browsermedia has no PTZ.
 *
 * Returns `undefined` when:
 *   - `discovery` is missing
 *   - `discovery` has neither `video` nor (ONVIF) `control`
 *   - browsermedia `discovery.video.path` is missing/empty
 *   - known-model branch can't materialize (delegates to
 *     `buildKnownModelCandidate`'s own undefined return)
 *
 * Caller (`handleBulkCommit`, W7) skips devices that return `undefined`
 * and emits a `console.warn`.
 *
 * @param discovery - The discovery row to bulk-add. Either
 *   `Discovery$Video` (video aspect) or `Discovery$Control` (ONVIF).
 * @param template - Optional device-registry template (only used by the
 *   known-model branch — passed through to `buildKnownModelCandidate`).
 * @param defaultHost - Host to use as the fallback when assembling URIs
 *   (e.g. ONVIF best-guess RTSP). Falls back to `discovery.address` and
 *   then to the discovery's video/control host if not provided.
 */
export const buildBulkCandidate = (
  discovery: T_Maybe<Discovery>,
  template?: T_Maybe<Template>,
  defaultHost?: T_Maybe<string>,
  // Brand-detected input (extended discovery). When the caller resolved
  // the detected brand's generic "Other" model + template, this fires the
  // brand-detected branch below. A separate param (not an overload of
  // `template`) keeps the known-model path unchanged. Per `think-in-systems`.
  brandDetected?: T_Maybe<{ modelId: T_Maybe<Model['id']>; template: T_Maybe<Template> }>
): T_Maybe<T_Optional<Pick<Minimist, 'video' | 'control' | 'modelId'>>> => {
  if (!discovery) return undefined;

  // A known model supplies stream and PTZ defaults for native discoveries.
  // Iris linking remains a separate route; only its identity may be reused.
  if (!!discovery.modelId && !discovery.irisEnabled && !!template) {
    const connection = discovery.video ?? discovery.control;
    const connectionHost = connection && 'host' in connection ? connection.host : undefined;
    const host = defaultHost ?? discovery.address ?? connectionHost;
    const known = buildKnownModelCandidate(discovery, template, undefined, host);
    if (known) {
      // ONVIF discovers the stream; a known driver's PTZ endpoint comes from
      // the same normalization as single-add, never the ONVIF probe's port.
      return {
        ...known,
        modelId: discovery.modelId,
        control: resolveControlSettings(undefined, template, known.video) as Minimist['control']
      };
    }
    // Fall through if the known-model helper bailed (e.g. template has
    // no video for the RTSP branch). The caller can still build a
    // best-guess candidate via the protocol branches below.
  }

  // Keep ONVIF control and a provisional stream until capability discovery succeeds.
  // Authentication can resolve the real URI later without changing the selected transport.
  if ('control' in discovery && !!discovery.control) {
    const control = discovery.control as OnvifConfig;
    const host = defaultHost ?? discovery.address ?? control.host;
    if (!host) return undefined;
    return {
      // Best-guess RTSP URI. The bulk-commit pipeline overrides
      // candidate.video with the probe-discovered URI when available
      // (see wiring.ts after the buildBulkCandidate call).
      video: {
        host,
        port: 554,
        path: '/',
        protocol: VideoProtocol.RTSP,
        credentials: control.credentials
      },
      control: { ...control },
      // Keep the selected ONVIF endpoint while carrying the resolved driver.
      ...(discovery.modelId ? { modelId: discovery.modelId } : {})
    };
  }

  // Video-aspect discoveries (NDI / RTSP family / browsermedia).
  if (!('video' in discovery) || !discovery.video) return undefined;

  const video = discovery.video;
  const videoHost = (video as { host?: string }).host;
  const host = defaultHost ?? discovery.address ?? videoHost;

  // Brand-detected branch (extended discovery — Multi-Add). A discovery
  // the encoder matched to a BRAND fingerprint but not a specific model
  // carries `brandId` with no `modelId`. When the caller resolved the
  // brand's generic "Other" model + registry template, commit THAT
  // instead of the blank protocol defaults below, so the tile reads
  // "{Brand} / Other" with the registry URX. Placed AFTER the ONVIF
  // branch and gated on a VIDEO aspect (we are past the video-aspect
  // guard) so it hijacks neither an ONVIF control-aspect source that also
  // carries a brandId (that returned above) nor a known-model discovery
  // (still has modelId → took the known-model branch above). Falls
  // through to the protocol defaults when the template cannot materialize
  // a video block (missing / loading / errored) so intent-loss is a
  // documented, tested fallback rather than a silent drop.
  // Per `reference-rules-in-comments` (ai.knowledge/global): peer of the
  // known-model branch; the "Other" name-match rule is PR 1023
  // manufacturer/details.tsx.
  if (
    !discovery.modelId &&
    !discovery.irisEnabled &&
    !!discovery.brandId &&
    !!brandDetected?.modelId &&
    !!brandDetected?.template
  ) {
    const built = buildBrandDetectedCandidate(
      discovery,
      brandDetected.modelId,
      brandDetected.template,
      host
    );
    if (built) return built;
    // Template resolved but produced no video block → fall through to the
    // protocol defaults (rtsp /1 + VISCA 52381 below). No add is lost.
  }

  // Browsermedia (local source) branch. Per plan §1.5: defensive guard —
  // missing/empty path means we cannot build a usable URX. Caller skips
  // + logs (no commit).
  if ('protocol' in video && video.protocol === VideoProtocol.Local) {
    if (!video.path || video.path.length === 0) return undefined;
    // Browsermedia has no PTZ → no control. Clone to avoid immer draft
    // identity tangles in the store.
    return { video: { ...video } };
  }

  // NDI branch. Clone video as-is (no credentials in NDI video config),
  // default control to VISCA UDP (plan §1.5 — canonical no-auth control).
  if ('protocol' in video && video.protocol === VideoProtocol.NDI) {
    if (!host) return undefined;
    return {
      video: { ...video },
      control: defaults({ host }, ViscaIpDefaults)
    };
  }

  // RTSP family (RTSP / RTSPT / RTSPLT / IRIS-as-RTSP). Use video as-is —
  // credentials may already be folded in by the discovery substitution
  // path. Default control to VISCA UDP.
  if (
    'protocol' in video &&
    (video.protocol === VideoProtocol.RTSP ||
      video.protocol === VideoProtocol.RTSPT ||
      video.protocol === VideoProtocol.RTSPLT ||
      video.protocol === VideoProtocol.IRIS)
  ) {
    if (!host) return undefined;
    return {
      video: { ...video },
      control: defaults({ host }, ViscaIpDefaults)
    };
  }

  // Unrecognized protocol → caller can skip+log.
  return undefined;
};

/**
 * Auto-configured field record — the shape `discovery.tsx`'s substitution
 * effects produce and dispatch into the `autoConfiguredFields` depot slice
 * (Multi-Add Phase 2 — auto-configured field labeling).
 *
 * Each entry maps a `fieldPath` (`"manufacturer.brand"`, `"video.host"`,
 * `"control.port"`, …) to the canonical auto-filled value. The settings-
 * panel's `<AutoConfiguredLabel>` looks up `(deviceId, fieldPath)` in the
 * slice and renders the small green tag when it finds the recorded value.
 *
 * Per `think-in-systems` (ai.knowledge/global): pure data structure;
 * functional helpers (`computeKnownModelAutoConfigured`,
 * `computeOnvifCapabilitiesAutoConfigured`) produce arrays of these for
 * the substitution effects to dispatch.
 */
export interface AutoConfiguredEntry {
  /** Field path (e.g. `"manufacturer.brand"`, `"video.host"`). */
  fieldPath: string;
  /** Canonical auto-filled value the slice stores for the eviction-on-edit comparison. */
  value: string;
}

/**
 * Internal shape accepted by `collectVideoEntries` — the four
 * source-of-truth video fields, each independently optional. Wider than
 * the canonical RTSP/NDI/Local Configuration unions so the helper can
 * accept partial / Settings-shaped inputs (e.g. the `candidate.video`
 * draft inside the substitution effect's producer callback, where every
 * field is `T_Optional`).
 *
 * Per `code-comments-required` (ai.knowledge/global): a local type
 * narrowed to ONLY the four fields the helper reads keeps the public
 * surface minimal — callers that pass a strict Configuration still
 * type-check via structural width.
 */
type VideoFieldsLike = {
  protocol?: T_Maybe<string | number>;
  host?: T_Maybe<string>;
  port?: T_Maybe<number>;
  path?: T_Maybe<string>;
};

/**
 * Read `video.protocol`, `video.host`, `video.port`, `video.path` from a
 * resolved video Configuration and emit one `AutoConfiguredEntry` per
 * defined field.
 *
 * Used by both single-add substitution effects (ONVIF capabilities and
 * known-model RTSP) to discriminate source-derived video fields. NDI
 * Configurations carry `host`/`protocol` (no `port`/`path`); RTSP-family
 * carries the full set. Undefined fields are silently skipped — the slice
 * only records what was actually populated.
 *
 * Per `code-comments-required` (ai.knowledge/global): the function is pure;
 * no immer / depot dependency; framework-agnostic so the test suite can
 * call it directly with a hand-built Configuration.
 */
const collectVideoEntries = (video: T_Maybe<VideoFieldsLike>): AutoConfiguredEntry[] => {
  const entries: AutoConfiguredEntry[] = [];
  if (!video) return entries;
  // protocol is always present on a valid Configuration (enum value).
  if (video.protocol !== undefined && video.protocol !== null) {
    entries.push({ fieldPath: 'video.protocol', value: String(video.protocol) });
  }
  // host is required on RTSP/NDI/Local Configurations — guard anyway so
  // a malformed input doesn't synthesize an empty `video.host` entry.
  if (typeof video.host === 'string' && video.host.length > 0) {
    entries.push({ fieldPath: 'video.host', value: video.host });
  }
  // port + path are RTSP-only (NDI carries `name` instead). Guard each
  // independently — `defaults` may leave them undefined on a partial URI.
  if (typeof video.port === 'number') {
    entries.push({ fieldPath: 'video.port', value: String(video.port) });
  }
  if (typeof video.path === 'string' && video.path.length > 0) {
    entries.push({ fieldPath: 'video.path', value: video.path });
  }
  return entries;
};

/** Internal shape accepted by `collectControlEntries` — three fields. */
type ControlFieldsLike = {
  protocol?: T_Maybe<string | number>;
  host?: T_Maybe<string>;
  port?: T_Maybe<number>;
};

/**
 * Read `control.protocol`, `control.host`, `control.port` from a resolved
 * control Configuration and emit one `AutoConfiguredEntry` per defined
 * field.
 *
 * `control.protocol` is recorded when the template provided it
 * (HTTP/VISCA_IP/ONVIF); `control.host` + `control.port` are recorded
 * when the template's transport carries them.
 *
 * Per `code-comments-required` (ai.knowledge/global): pure; framework-
 * agnostic.
 */
const collectControlEntries = (control: T_Maybe<ControlFieldsLike>): AutoConfiguredEntry[] => {
  const entries: AutoConfiguredEntry[] = [];
  if (!control) return entries;
  if (control.protocol !== undefined && control.protocol !== null) {
    entries.push({ fieldPath: 'control.protocol', value: String(control.protocol) });
  }
  if (typeof control.host === 'string' && control.host.length > 0) {
    entries.push({ fieldPath: 'control.host', value: control.host });
  }
  if (typeof control.port === 'number') {
    entries.push({ fieldPath: 'control.port', value: String(control.port) });
  }
  return entries;
};

/**
 * Compute the auto-configured field entries for the single-add known-model
 * HTTP-probe substitution branch (Multi-Add Phase 2 — auto-configured
 * field labeling).
 *
 * Mirrors the multi-add path's known-model field set so the settings-
 * panel `<AutoConfiguredLabel>` mounts see the same record shape
 * regardless of which add flow committed the device:
 *
 *   - `manufacturer.brand` — from `template.model.brandId`
 *   - `manufacturer.model` — from `selection.modelId` (or `template.model.id`)
 *   - `video.protocol` / `host` / `port` / `path` — only when
 *     `template.video` was non-undefined (discriminates template-derived
 *     RTSP fields from `RtspDefaults` / `NdiDefaults` applied via
 *     `defaultsDeep`, which are NOT source-derived per the plan
 *     'source-of-truth table' notes).
 *   - `control.protocol` / `host` / `port` — only when `template.control`
 *     was non-undefined (discriminates template-derived VISCA/HTTP/ONVIF
 *     transport fields from `ViscaIpDefaults` defaults).
 *
 * The `substitution` argument is the helper's own
 * `buildKnownModelCandidate` return value — passing it in (instead of
 * re-deriving inside) keeps this function purely composable from a unit
 * test that doesn't need to set up the registry mock.
 *
 * Per `think-in-systems` (ai.knowledge/global): the helper is composable
 * with `buildKnownModelCandidate`. Per `stay-in-scope`
 * (ai.knowledge/workflow): substitution semantics in `discovery.tsx` are
 * unchanged — this helper only describes WHICH of the substituted fields
 * were source-derived for the label.
 *
 * @param selection - The Discovery the user picked. Must have `modelId`
 *   for the caller to have reached this branch (defensive guard kept here
 *   for unit-test safety).
 * @param template - Device-registry template for `selection.modelId`. The
 *   `template.video` / `template.control` undefined-checks act as the
 *   discriminator between source-derived and defaults-applied fields.
 * @param substitution - The `{ video, control }` slice returned by
 *   `buildKnownModelCandidate`. Used to read the canonical values that
 *   actually landed on the candidate.
 * @returns Ordered list of `AutoConfiguredEntry` records. Empty when
 *   `selection.modelId` is missing or `template` is absent (defensive —
 *   the caller's outer guards already prevent these cases in production).
 */
export const computeKnownModelAutoConfigured = (
  selection: T_Maybe<Discovery>,
  template: T_Maybe<Template>,
  substitution: T_Maybe<{
    video?: T_Maybe<VideoFieldsLike>;
    control?: T_Maybe<ControlFieldsLike>;
  }>
): AutoConfiguredEntry[] => {
  const entries: AutoConfiguredEntry[] = [];
  if (!selection || !template || !substitution) return entries;

  // manufacturer.brand / manufacturer.model — both always source-derived
  // for a fingerprint-matched discovery. `template.model.brandId` is the
  // canonical brand id (the discovery itself does not carry brand —
  // `useTemplate(modelId)` resolves it via the registry).
  const brandId = template.model.brandId;
  if (brandId !== undefined && brandId !== null) {
    entries.push({ fieldPath: 'manufacturer.brand', value: String(brandId) });
  }
  const modelId = selection.modelId ?? template.model.id;
  if (modelId !== undefined && modelId !== null) {
    entries.push({ fieldPath: 'manufacturer.model', value: String(modelId) });
  }

  // video.* — only when the template supplied video facts. Per plan
  // §1.5: RtspDefaults/NdiDefaults are NOT source-derived; the absence of
  // `template.video` means `buildKnownModelCandidate` returned defaults-
  // only on the video aspect (or the NDI branch which uses `selection.video`,
  // also source-derived — handled below).
  //
  // NDI branch: `selection.video` IS the source (the NDI discovery
  // packet's transport fields are source-of-truth). RTSP branch: only
  // `template.video` is the source. The two branches diverge in which
  // fields qualify as "auto-configured", so we discriminate here:
  const isNdi = 'video' in selection && selection.video?.protocol === VideoProtocol.NDI;
  if (isNdi || template.video !== undefined) {
    entries.push(...collectVideoEntries(substitution.video));
  }

  // control.* — only when the template supplied control facts. Per plan
  // §1.5: ViscaIpDefaults applied via `defaultsDeep` are NOT source-
  // derived. `template.control` non-undefined means the registry shipped
  // an HTTP/VISCA_IP/ONVIF transport contract for this model.
  if (template.control !== undefined) {
    entries.push(...collectControlEntries(substitution.control));
  }

  return entries;
};

/**
 * Compute the auto-configured field entries for the single-add ONVIF
 * capabilities substitution branch (Multi-Add Phase 2 — auto-configured
 * field labeling).
 *
 * The ONVIF probe writes `capabilities.discovery.uri`; `discovery.tsx`
 * converts it to a video Configuration via
 * `toConfiguration(defaults({}, uri, credentials))` and assigns to
 * `candidate.video`. All four `video.*` fields (protocol/host/port/path)
 * came from the camera's own GetServices/GetStreamUri response — they
 * are unambiguously source-derived and should carry the label.
 *
 * Per `stay-in-scope` (ai.knowledge/workflow): the substitution itself is
 * untouched — this helper only computes WHICH fields to mark.
 *
 * @param video - The resolved video Configuration written to
 *   `candidate.video`. Pass it AFTER `toConfiguration(...)` so the helper
 *   sees the canonical values the label will compare against on user-
 *   edit eviction.
 * @returns Ordered list of `AutoConfiguredEntry` records — `video.protocol`
 *   first, then `host`/`port`/`path` in declaration order.
 */
export const computeOnvifCapabilitiesAutoConfigured = (
  video: T_Maybe<VideoFieldsLike>
): AutoConfiguredEntry[] => collectVideoEntries(video);
